import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Shield, CreditCard, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const [subInfo, setSubInfo] = useState(null);
    const [showCancel, setShowCancel] = useState(false);

    const isAdmin = user?.role === "admin";

    useEffect(() => {
        const fetchSub = async () => {
            try {
                const { data } = await axios.get(`${API}/subscription/info`, { withCredentials: true });
                setSubInfo(data);
            } catch { /* silent */ }
        };
        fetchSub();
    }, []);

    const status = subInfo?.subscription?.status || user?.subscription_status || "trial";

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="settings-page">
            <Navbar />
            <div className="max-w-2xl mx-auto px-6 py-12">
                <h1 className="text-2xl font-bold tracking-tight text-white mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Settings
                </h1>

                {/* Profile */}
                <div className="border border-white/10 bg-[#121212] rounded-2xl p-6 mb-6" data-testid="profile-section">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Profile</p>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#007AFF]/20 flex items-center justify-center">
                                <User className="w-5 h-5 text-[#007AFF]" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-white">{user?.name}</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-xs text-zinc-500">{user?.email}</p>
                                    {isAdmin && <Badge className="bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30 rounded-full text-[9px] px-2 py-0">Admin</Badge>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Subscription - Only for non-admin */}
                {!isAdmin && (
                    <div className="border border-white/10 bg-[#121212] rounded-2xl p-6 mb-6" data-testid="subscription-section">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Subscription</p>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <CreditCard className="w-5 h-5 text-zinc-400" />
                                <div>
                                    <p className="text-sm font-semibold text-white">TradeNalysis Pro</p>
                                    <p className="text-xs text-zinc-500">$19/month</p>
                                </div>
                            </div>
                            <Badge variant="outline" className={`rounded-full text-[10px] tracking-widest uppercase font-bold border ${status === 'active' ? 'text-[#00C805] border-[#00C805]/30' : status === 'trial' ? 'text-[#007AFF] border-[#007AFF]/30' : 'text-[#FF3B30] border-[#FF3B30]/30'}`}>
                                {status}
                            </Badge>
                        </div>
                        <p className="text-xs text-zinc-400 mb-4">{subInfo?.subscription?.message || `Status: ${status}`}</p>

                        {status !== "active" && (
                            <Button
                                onClick={() => navigate("/pricing")}
                                className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl font-bold"
                                data-testid="settings-subscribe-btn"
                            >
                                Subscribe Now
                            </Button>
                        )}

                        {status === "active" && (
                            <>
                                <Separator className="bg-white/10 my-4" />
                                {!showCancel ? (
                                    <button
                                        onClick={() => setShowCancel(true)}
                                        className="text-xs text-zinc-500 hover:text-[#FF3B30] transition-colors"
                                        data-testid="show-cancel-btn"
                                    >
                                        Cancel subscription
                                    </button>
                                ) : (
                                    <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-xl p-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-semibold text-white mb-1">Cancel Subscription?</p>
                                                <p className="text-xs text-zinc-400 mb-3">Your access will continue until the end of the current billing period. You won't be charged again.</p>
                                                <div className="flex gap-2">
                                                    <Button
                                                        onClick={() => { toast.info("Subscription will not renew at end of period."); setShowCancel(false); }}
                                                        className="bg-[#FF3B30] hover:bg-[#FF645A] text-white rounded-xl text-xs h-8 px-4"
                                                        data-testid="confirm-cancel-btn"
                                                    >
                                                        Confirm Cancel
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        onClick={() => setShowCancel(false)}
                                                        className="text-zinc-400 hover:text-white rounded-xl text-xs h-8"
                                                        data-testid="keep-sub-btn"
                                                    >
                                                        Keep Subscription
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Security */}
                <div className="border border-white/10 bg-[#121212] rounded-2xl p-6" data-testid="security-section">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Security</p>
                    <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-zinc-400" />
                        <div>
                            <p className="text-sm font-semibold text-white">Password</p>
                            <p className="text-xs text-zinc-500">Change your account password</p>
                        </div>
                    </div>
                    <Separator className="bg-white/10 my-4" />
                    <Button
                        variant="ghost"
                        onClick={async () => {
                            await logout();
                            navigate("/");
                        }}
                        className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-xl text-sm"
                        data-testid="settings-logout-btn"
                    >
                        Sign Out
                    </Button>
                </div>
            </div>
        </div>
    );
}
